import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDateRange } from '../lib/format'
import type { Tour, WithId } from '../types/models'
import Poster from './Poster'

/* ============================================================================
   A wall of tours.

   Replaces the horizontal shelf this used to be. A rail shows four posters and
   hides the rest behind a gesture — fine for browsing something you don't
   need, wrong for a back catalogue somebody is looking through. It also fought
   the page: a sideways flick on the posters dragged the whole layout.

   So: a grid that fills the width and grows downward, capped at two rows with
   a control to open the rest. The cap exists because after a backfill this is
   a career, and a hundred posters is not a section, it is a page.
   ============================================================================ */

/** Two rows at the widest column count, which is what the cap is really for. */
const COLLAPSED = 12

export default function TourGrid({
  tours,
  emptyMessage,
}: {
  tours: Array<WithId<Tour>>
  emptyMessage?: string
}) {
  const [open, setOpen] = useState(false)

  if (tours.length === 0) {
    return emptyMessage ? <p className="panel panel-pad t-caption">{emptyMessage}</p> : null
  }

  const shown = open ? tours : tours.slice(0, COLLAPSED)
  const hidden = tours.length - shown.length

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div
        style={{
          display: 'grid',
          // Sized so posters stay a readable width and the row count follows
          // the window rather than a breakpoint.
          gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
          gap: 18,
          // Without this, grid items stretch to the tallest in the row and the
          // aspect ratio on the artwork stops governing, so posters come out
          // different heights depending on how their titles wrap.
          alignItems: 'start',
        }}
      >
        {shown.map((tour) => (
          <Link
            key={tour.id}
            to={`/tours/${tour.id}`}
            className="stack past-tour"
            style={{ gap: 9, minWidth: 0 }}
          >
            {/* Fills its column rather than taking a fixed width, so the grid
                stays even whatever the artwork's own proportions are. */}
            <div style={{ aspectRatio: '1 / 1.42', width: '100%' }}>
              <Poster url={tour.posterURL} width={220} alt={tour.tourName} fill />
            </div>
            <span className="stack" style={{ gap: 2, minWidth: 0 }}>
              <span className="truncate" style={{ fontWeight: 600, fontSize: '0.92rem' }}>
                {tour.tourName}
              </span>
              <span className="t-spec-caption dim truncate">
                {formatDateRange(tour.startDate, tour.endDate)}
              </span>
            </span>
          </Link>
        ))}
      </div>

      {hidden > 0 && (
        <button
          className="btn btn-secondary"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setOpen(true)}
        >
          Show {hidden} more
        </button>
      )}

      {open && tours.length > COLLAPSED && (
        <button
          className="btn btn-quiet"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setOpen(false)}
        >
          Show fewer
        </button>
      )}
    </div>
  )
}
